import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, LockOpen, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function PeriodLockPanel() {
  const [lock, setLock] = useState(null);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/period-lock");
      setLock(r.data);
      setDate((prev) => prev || r.data?.locked_until || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load lock"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const r = await api.put("/admin/period-lock", { locked_until: clear ? null : date || null, reason: reason || null });
      setLock(r.data);
      setDate(r.data?.locked_until || "");
      setReason("");
      toast.success(r.data?.locked_until ? `Books locked up to ${r.data.locked_until}` : "Period lock removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const locked = !!lock?.locked_until;
  return (
    <div className={`border-2 rounded-lg p-6 space-y-4 ${locked ? "border-[#2C1810] bg-[#F7F2E8]" : "border-[#EAE5D9] bg-white"}`} data-testid="period-lock-panel">
      <div className="flex items-start gap-3">
        {locked ? <Lock className="w-6 h-6 text-[#2C1810] shrink-0 mt-0.5" /> : <LockOpen className="w-6 h-6 text-[#8B7355] shrink-0 mt-0.5" />}
        <div className="flex-1">
          <h2 className="font-[Outfit] text-xl font-semibold">Accounting Period Lock</h2>
          <p className="text-sm text-[#5C4033] mt-1">
            Once a date is set, <b>no one — including Super Admin —</b> can create, edit, delete or void any Sales invoice, Purchase bill, PO, GRN, Dispatch, Voucher, Expense or Payment dated on or before it. Move or clear the lock here to reopen a period.
          </p>
          <p className="text-xs mt-2" data-testid="period-lock-status">
            {locked
              ? <span className="text-[#2C1810] font-semibold">Books closed up to {lock.locked_until}{lock.updated_by ? ` · set by ${lock.updated_by}` : ""}{lock.reason ? ` · "${lock.reason}"` : ""}</span>
              : <span className="text-[#8B7355]">No period is locked — all dates are open for entry.</span>}
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-[180px_1fr_auto_auto] gap-3 items-end">
        <div>
          <Label>Books locked up to</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="period-lock-date" />
        </div>
        <div>
          <Label>Reason (optional)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. GSTR-3B filed for March" data-testid="period-lock-reason" />
        </div>
        <Button onClick={() => save(false)} disabled={saving || !date} className="bg-[#2C1810] hover:bg-[#1a0e09] text-white" data-testid="period-lock-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-1.5" /> Lock books</>}
        </Button>
        {locked && (
          <Button variant="outline" onClick={() => save(true)} disabled={saving} data-testid="period-lock-clear-btn">
            <LockOpen className="w-4 h-4 mr-1.5" /> Remove lock
          </Button>
        )}
      </div>
    </div>
  );
}
